import * as React from 'react';
import * as UI from 'core/ui';

import { ClockingUserDTO, Res, WorkingPeriodStatusDTO } from 'core/api';
import { Content, Header, PosScreen, Timer, UserButton } from 'pos/ui';
import * as Images from 'pos/Images';
import { AttendanceFacade } from 'pos/facades';
import * as Clocking from './Clocking';
import { Modal } from 'core/ui-next/modal';
import { CardPanel, BasicButton, SvgIcon } from 'core/ui-next';
import { VStack, Text, Box } from '@chakra-ui/react';
import * as CoreImages from 'core/img';
import editClocking from './editClocking';
import { DateUtils } from 'core/utils';

type Props = { user: ClockingUserDTO, status: WorkingPeriodStatusDTO } & Modal.Actions<void>;


type ScreenMode = 'start' | 'stop' | 'completed';

function getScreenMode(status: WorkingPeriodStatusDTO): ScreenMode {
	if (status.ClockIn === null)
		return 'start';

	if (status.ClockOut === null)
		return 'stop';

	return 'completed';
}

function getScreenTitle(mode: ScreenMode, user: ClockingUserDTO): string {
	if (mode === 'start')
		return Res.Web.CLOCKING_VIEW_REGISTER_CLOCK_IN_LBL();
	if (mode === 'stop')
		return Res.Web.CLOCKING_VIEW_REGISTER_CLOCK_OUT_LBL();
	return Res.Web.CLOCKING_VIEW_REGISTER_WORKING_PERIOD_COMPLETED_LBL(user.Name);
}

function getScreenIcon(mode: ScreenMode, user: ClockingUserDTO): React.ReactNode {
	if (mode === 'completed')
		return <SvgIcon src={CoreImages.Success} color='success' h='12' w='12' shadow={'md'} rounded={'full'} />;
	return <UserButton user={user} onSelect={() => { }} />;
}

function RegisterClockingData(props: Props) {

	const { cancel, user } = props;

	const [status, setStatus] = React.useState(props.status);
	const screenRef = React.useRef<HTMLInputElement>(null);

	const screenMode = getScreenMode(status);

	const onClockIn = React.useCallback(async () => {
		setStatus(await AttendanceFacade.clockIn(Clocking.addClockIn(status, new Date())));
	}, [status]);

	const onClockOut = React.useCallback(async () => {
		setStatus(await AttendanceFacade.clockOut(Clocking.addClockOut(status, new Date())));
	}, [status]);

	const onEditClocking = React.useCallback(async () => {
		const result = await editClocking(status);

		if (!result.ok)
			return;

		if (screenMode === 'start')
			setStatus(await AttendanceFacade.clockIn(result.value));
		else if (screenMode === 'stop')
			setStatus(await AttendanceFacade.clockOut(result.value));


	}, [status, screenMode]);



	const buttonClick = screenMode === 'start' ? onClockIn : onClockOut;
	const buttonIcon = screenMode === 'start' ? CoreImages.Play : CoreImages.Stop;
	const buttonText = screenMode === 'start' ? Res.Web.CLOCKING_VIEW_REGISTER_CLOCK_IN_BTN() : Res.Web.CLOCKING_VIEW_REGISTER_CLOCK_OUT_BTN();
	const cardButton = screenMode === 'completed' ? null : <BasicButton type={screenMode === 'start' ? 'success' : 'secondary'} w='80' icon={buttonIcon} text={buttonText} onClick={buttonClick} />;

	const cardTitle = screenMode === 'completed' ? Res.Web.CLOCKING_VIEW_REGISTER_WORKING_PERIOD_COMPLETED_CARD_LBL() : DateUtils.formatDate(status.BusinessDay, 'fullDate');
	const cardContent = screenMode === 'completed' ? <Text color={{ base: '{colors.neutral.800}', _dark: 'white' }} fontWeight={'bold'} fontSize={'2xl'}>{UI.Format.asTime(status.ClockIn)} - {UI.Format.asTime(status.ClockOut)}</Text> : <Timer startDate={status.ClockIn} />;
	const cardHeight = screenMode === 'completed' ? '28' : '32';

	return (
		<PosScreen screenId='REGISTER_CLOCKING_DATA' ref={screenRef} size={'small'}>

			<Header title={Res.Web.CLOCKING_VIEW()} image={Images.AgoraLogo} onClose={() => cancel()} />

			<Content>
				<VStack width={'full'} height={'full'} gap='6' paddingTop={'14'} paddingBottom={'6'} paddingX={'6'}>

					<VStack width={'full'} gap={'4'} justifyContent={'center'}>
						{getScreenIcon(screenMode, user)}
						<Text fontSize={screenMode === 'completed' ? 'sm' : 'md'} fontWeight={screenMode === 'completed' ? 'semibold' : 'bold'} textAlign={'center'}>{getScreenTitle(screenMode, user)}</Text>
					</VStack>

					<CardPanel w='80' h={cardHeight} gap='6' position={'relative'}>
						<Box h='4.5' w='4.5' position={'absolute'} top='1.5' right='2' _hover={{ bg: { base: '{colors.neutral.200}', _dark: '{colors.neutral.700}' } }} cursor={'pointer'}>
							<SvgIcon h='4.5' w='4.5' src={Images.JustPointMore} color={{ base: '{colors.neutral.500}', _dark: '{colors.neutral.400}' }} onClick={onEditClocking} />
						</Box>
						<Text fontSize={'sm'} fontWeight={'bold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{cardTitle}</Text>
						{cardContent}
					</CardPanel>

					{cardButton}

				</VStack>
			</Content>


		</PosScreen>
	);
}

export async function registerClockingData(user: ClockingUserDTO) {
	const status = await AttendanceFacade.getWorkingPeriodStatus(user.Id);
	return Modal.show(RegisterClockingData, { status, user });
}
